import React, {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';
import { Badge, Button, Modal, Segmented, Select, Space, Tag, Tooltip, Typography } from 'antd';
import { CloseOutlined, CloudUploadOutlined, DesktopOutlined, MobileOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';

import { type CatalogueDesignKey } from '@eshop/designs';
import { logInfo } from '@tools/LogsHandler.tsx';
import { ButtonPanelConfigEstilo, type ButtonPanelConfigEstiloRef } from '@components/settings/RenderEstiloConfig.tsx';
import LayoutEditorTabs, { type LayoutEditorTabKey } from './LayoutEditorTabs';
import { HERO_ASPECT_RATIO_OPTIONS } from '../constants/heroAspectRatios';

const { Text, Title } = Typography;

type Option = { label: string; value: string };

type DesignOption = { key: CatalogueDesignKey; label: string; description?: string };

interface LayoutEditorModalProps {
  mode?: 'shop' | 'web';
  visibleTabs?: LayoutEditorTabKey[];
  open: boolean;
  onClose: () => void;
  onMinimize: () => void;
  onTabChange: (key: LayoutEditorTabKey) => void;
  activeTab: LayoutEditorTabKey;
  templateValue: string;
  templateOptions: Option[];
  onTemplateChange: (value: string) => void;
  designValue: CatalogueDesignKey;
  designOptions: DesignOption[];
  onDesignChange: (design: CatalogueDesignKey) => void;
  isSaving: boolean;
  isLoading: boolean;
  allowPublish: boolean;
  hasDraftChanges: boolean;
  savedAt: string | null;
  updatedAt: string | null;
  publishedAt: string | null;
  onPublish: () => void;
  sectionsTab: ReactNode;
  styleTab: ReactNode;
  colorsTab: ReactNode;
  viewTab: ReactNode;
}

interface LayoutEditorStylePanelProps {
  typographyPanel?: ReactNode;
  bannerAspectClass: string | null;
  bannerAspectDefaultRatio: string | null;
  onBannerAspectChange: (value: string | null) => void;
  itemAspectRatio: string;
  onItemAspectChange: (value: string) => void;
  itemCardSize: string | null;
  onItemCardSizeChange: (value: string | null) => void;
}

interface LayoutEditorViewPanelProps {
  device: 'desktop' | 'mobile';
  onDeviceChange: (value: 'desktop' | 'mobile') => void;
  activeDesignLabel: string;
}

type LayoutEditorModalComponent = React.FC<LayoutEditorModalProps> & {
  StylePanel: React.FC<LayoutEditorStylePanelProps>;
  ColorsPanel: React.FC<LayoutEditorColorsPanelProps>;
  ViewPanel: React.FC<LayoutEditorViewPanelProps>;
};

interface LayoutEditorColorsPanelProps {
  onAfterSave?: () => void;
  colorsPanelRef?: React.RefObject<ButtonPanelConfigEstiloRef | null>;
  onDirtyChange?: (dirty: boolean) => void;
}



const LayoutEditorModalComponent: React.FC<LayoutEditorModalProps> = ({
  mode = 'shop',
  visibleTabs,
  open,
  onClose,
  onMinimize,
  onTabChange,
  activeTab,
  // templateValue,
  // templateOptions,
  // onTemplateChange,
  designValue,
  designOptions,
  onDesignChange,
  isSaving,
  isLoading,
  allowPublish,
  hasDraftChanges,
  savedAt,
  // updatedAt,
  publishedAt,
  onPublish,
  sectionsTab,
  styleTab,
  colorsTab,
  viewTab,
}) => {
  // console.log('[DEBUG] LayoutEditorModal Render', { allowPublish, isSaving, isLoading, hasDraftChanges, onPublishDefined: !!onPublish });
  const tablistDescriptionId = useId();
  const modalContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const { body } = document;
    if (!body) {
      return;
    }

    const previousOverflow = body.style.overflow;
    const previousPaddingRight = body.style.paddingRight;
    const hadScrollingEffect = body.classList.contains('ant-scrolling-effect');
    const hadModalOpen = body.classList.contains('ant-modal-open');

    body.style.overflow = '';
    body.style.paddingRight = '';
    body.classList.remove('ant-scrolling-effect');
    body.classList.remove('ant-modal-open');

    return () => {
      const otherVisibleModals = document.querySelectorAll(
        '.ant-modal-root .ant-modal-wrap[aria-hidden="false"]',
      ).length;

      if (otherVisibleModals > 0) {
        if (previousOverflow) {
          body.style.overflow = previousOverflow;
        }
        if (previousPaddingRight) {
          body.style.paddingRight = previousPaddingRight;
        }
        if (hadScrollingEffect) {
          body.classList.add('ant-scrolling-effect');
        }
        if (hadModalOpen) {
          body.classList.add('ant-modal-open');
        }
        return;
      }

      body.style.removeProperty('overflow');
      body.style.removeProperty('padding-right');
      body.classList.remove('ant-scrolling-effect');
      body.classList.remove('ant-modal-open');
    };
  }, [open]);

  const designOptionMap = useMemo(() => {
    return designOptions.reduce<Record<CatalogueDesignKey, DesignOption>>((acc, option) => {
      acc[option.key] = option;
      return acc;
    }, {} as Record<CatalogueDesignKey, DesignOption>);
  }, [designOptions]);

  // const activeDesignLabel = designOptionMap[designValue]?.label ?? designValue;

  const handleAfterOpen = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        return;
      }
      window.requestAnimationFrame(() => {
        const container = modalContainerRef.current;
        if (!container) {
          return;
        }
        const firstTab = container.querySelector<HTMLButtonElement>('[role="tab"]');
        firstTab?.focus();
      });
    },
    [],
  );

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      width="min(400px, 92vw)"
      className="layout-editor-modal"
      rootClassName="layout-editor-modal-root"
      destroyOnHidden={false}
      forceRender
      mask={false}
      maskClosable={false}
      closable={false}
      keyboard={false}
      style={{ top: 24 }}
      title={null}
      afterOpenChange={handleAfterOpen}
      modalRender={(node) =>
        isValidElement(node)
          ? cloneElement(node, { 'aria-modal': false } as Record<
            string,
            unknown
          >)
          : node
      }
    >
      <div ref={modalContainerRef} className="layout-editor-modal__container">
        {/* Row 1: Title + Status Indicator + Actions */}
        <div className="layout-editor-modal__header-row">
          <Title level={5} style={{ margin: 0, flex: 1 }}>
            {mode === 'web' ? 'Editor de Web' : 'Editor de Tienda'}
          </Title>
          <Tooltip title={hasDraftChanges ? 'Cambios sin publicar' : 'Publicado'}>
            <Badge
              status={hasDraftChanges ? 'processing' : 'success'}
              style={{ marginRight: 8 }}
            />
          </Tooltip>
          <Space size={4}>
            <Tooltip title="Minimizar">
              <Button
                type="text"
                size="small"
                className="layout-editor-modal__action-btn"
                icon={
                  <span
                    aria-hidden="true"
                    className="layout-editor-modal__icon layout-editor-modal__icon--minimize"
                  />
                }
                onClick={onMinimize}
                aria-label="Minimizar editor"
              />
            </Tooltip>
            {mode === 'shop' && (
              <Tooltip title="Cerrar">
                <Button
                  type="text"
                  size="small"
                  className="layout-editor-modal__action-btn"
                  icon={<CloseOutlined />}
                  onClick={onClose}
                  aria-label="Cerrar editor"
                />
              </Tooltip>
            )}
          </Space>
        </div>

        {/* Row 2: Style Selector + Publish CTA */}
        <div className="layout-editor-modal__header-controls">
          {mode === 'shop' ? <Select
            value={designValue}
            options={designOptions.map((option) => ({
              label: option.label,
              value: option.key,
            }))}
            onChange={(value) => onDesignChange(value as CatalogueDesignKey)}
            aria-label="Seleccionar estilo visual"
            popupMatchSelectWidth={false}
            size="small"
            style={{ flex: 1, minWidth: 120 }}
          /> : null}
          {mode === 'shop' && designOptionMap[designValue]?.description ? (
            <Tooltip title={designOptionMap[designValue]?.description}>
              <Button
                type="text"
                size="small"
                icon={<span style={{ fontSize: 12 }}>ⓘ</span>}
                aria-label="Ver descripción del estilo"
              />
            </Tooltip>
          ) : null}
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            onClick={() => {
              logInfo('[DEBUG] Publicar Clicked');
              onPublish?.();
            }}
            loading={isSaving}
            disabled={!allowPublish || isLoading}
            size="small"
          >
            Publicar
          </Button>
        </div>

        {/* Tabs Content */}
        <div
          className="layout-editor-modal__content"
          aria-describedby={tablistDescriptionId}
        >
          <LayoutEditorTabs
            activeKey={activeTab}
            onChange={onTabChange}
            sectionsTab={sectionsTab}
            styleTab={styleTab}
            colorsTab={colorsTab}
            viewTab={viewTab}
            visibleTabs={visibleTabs}
          />
        </div>

        {/* Compact Footer with Status Info */}
        <div className="layout-editor-modal__footer-compact" id={tablistDescriptionId}>
          {hasDraftChanges ? (
            <Tag color="orange" style={{ margin: 0 }}>Cambios sin publicar</Tag>
          ) : (
            <Tag color="green" style={{ margin: 0 }}>Publicado</Tag>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {savedAt ? `Guardado ${savedAt}` : publishedAt ? `Publicado ${publishedAt}` : 'Sin cambios locales'}
          </Text>
        </div>
      </div>
    </Modal>
  );
};

const BANNER_ASPECT_INHERIT_VALUE = '__inherit__';

const LayoutEditorStylePanel: React.FC<LayoutEditorStylePanelProps> = ({
  typographyPanel,
  bannerAspectClass,
  bannerAspectDefaultRatio,
  onBannerAspectChange,
  itemAspectRatio,
  onItemAspectChange,
  itemCardSize,
  onItemCardSizeChange,
}) => {
  const selectValue = bannerAspectClass ?? BANNER_ASPECT_INHERIT_VALUE;
  const defaultRatio = bannerAspectDefaultRatio || '5:1';
  const defaultOptionLabel = `Por defecto (${defaultRatio})`;

  const bannerOptions = useMemo(
    () => [
      { label: defaultOptionLabel, value: BANNER_ASPECT_INHERIT_VALUE },
      ...HERO_ASPECT_RATIO_OPTIONS.map((option) => ({
        label: option.label,
        value: option.value,
      })),
    ],
    [defaultOptionLabel],
  );

  const itemOptions = [
    { label: 'Cuadrado (1:1)', value: 'aspect_1-1' },
    { label: 'Vertical (3:4)', value: 'aspect_3-4' },
    { label: 'Vertical (2:3)', value: 'aspect_2-3' },
    { label: 'Vertical (5:8)', value: 'aspect_5-8' },
    { label: 'Vertical (9:16)', value: 'aspect_9-16' },
    { label: 'Horizontal (4:3)', value: 'aspect_4-3' },
    { label: 'Horizontal (3:2)', value: 'aspect_3-2' },
    { label: 'Horizontal (8:5)', value: 'aspect_8-5' },
    { label: 'Horizontal (16:9)', value: 'aspect_16-9' },
  ];

  const cardSizeOptions = useMemo(
    () => [
      { label: 'Pequeña (6 por fila)', value: 'small' },
      { label: 'Normal (4 por fila)', value: 'normal' },
      { label: 'Grande (3 por fila)', value: 'large' },
    ],
    [],
  );

  const handleBannerChange = (value: string) => {
    onBannerAspectChange(value === BANNER_ASPECT_INHERIT_VALUE ? null : value);
  };

  return (
    <div className="layout-editor-style" role="region" aria-label="Formatos de imagen">
      <Space orientation="vertical" size={20} className="layout-editor-style__content">
        {typographyPanel ? (
          <Space orientation="vertical" size={6} style={{ width: '100%' }}>
            <Title level={5} style={{ marginBottom: 0 }}>
              Tipografía
            </Title>
            {typographyPanel}
          </Space>
        ) : null}
        <Space orientation="vertical" size={6} style={{ width: '100%' }}>
          <Title level={5} style={{ marginBottom: 0 }}>
            Formato de banners
          </Title>
          <Select
            aria-label="Formato de banners"
            value={selectValue}
            options={bannerOptions}
            onChange={handleBannerChange}
            style={{ width: '100%' }}
          />
        </Space>

        <Space orientation="vertical" size={6} style={{ width: '100%' }}>
          <Title level={5} style={{ marginBottom: 0 }}>
            Formato de items
          </Title>
          <Select
            aria-label="Formato de imágenes de items"
            value={itemAspectRatio}
            options={itemOptions}
            onChange={onItemAspectChange}
            style={{ width: '100%' }}
          />
        </Space>

        <Space orientation="vertical" size={6} style={{ width: '100%' }}>
          <Title level={5} style={{ marginBottom: 0 }}>
            Tamaño de cards
          </Title>
          <Select
            aria-label="Tamaño de cards en catálogo"
            value={itemCardSize ?? 'normal'}
            options={cardSizeOptions}
            onChange={(val) => onItemCardSizeChange(val)}
            style={{ width: '100%' }}
          />
        </Space>
      </Space>
    </div>
  );
};

const LayoutEditorColorsPanel: React.FC<LayoutEditorColorsPanelProps> = ({ onAfterSave, colorsPanelRef, onDirtyChange }) => (
  <div className="layout-editor-colors" role="region" aria-label="Paleta de colores">
    <ButtonPanelConfigEstilo
      ref={colorsPanelRef}
      variant="embedded"
      className="layout-editor-colors__panel"
      onAfterSave={onAfterSave}
      showSaveButton={false}
      onDirtyChange={onDirtyChange}
    />
  </div>
);

const LayoutEditorViewPanel: React.FC<LayoutEditorViewPanelProps> = ({
  device,
  onDeviceChange,
  activeDesignLabel,
}) => {
  const handleChange = (value: string | number) => {
    const next = (value as 'desktop' | 'mobile') ?? 'desktop';
    onDeviceChange(next);
  };

  return (
    <div className="layout-editor-view" role="region" aria-label="Vista previa">
      <Space orientation="vertical" size={16} className="layout-editor-view__content">
        <div>
          <Title level={5} style={{ marginBottom: 4 }}>
            Modo de visualización
          </Title>
          <Text type="secondary">
            Estilo actual: «{activeDesignLabel}». Cambios visibles en tiempo real.
          </Text>
        </div>
        <Segmented
          value={device}
          onChange={handleChange}
          options={[
            { label: <span className="layout-editor-view__device">Escritorio</span>, value: 'desktop', icon: <DesktopOutlined /> },
            { label: <span className="layout-editor-view__device">Mobile</span>, value: 'mobile', icon: <MobileOutlined /> },
          ]}
          aria-label="Cambiar modo de vista"
        />
      </Space>
    </div>
  );
};

const LayoutEditorModal = LayoutEditorModalComponent as LayoutEditorModalComponent;
LayoutEditorModal.StylePanel = LayoutEditorStylePanel;
LayoutEditorModal.ColorsPanel = LayoutEditorColorsPanel;
LayoutEditorModal.ViewPanel = LayoutEditorViewPanel;

export default LayoutEditorModal;
